/**
 * parseParkedDatabaseStatement — lightweight lexical scan that pulls the
 * database name + a tiny per-engine option subset out of the parked statement
 * text so the inline `[Edit Database]` popover can seed its form.
 *
 * DSE2's `extractDdlStructure` does not expose a structured database arm, so
 * the inline path scans the parked statement directly. The form's commit path
 * still goes through DSE2's `applyDdlEdit({ op: 'create-database' | … })`;
 * this scan is for SEED only.
 *
 * Recognises:
 *   - CREATE [PLUGGABLE] {DATABASE | SCHEMA} [IF NOT EXISTS] <name>
 *       [WITH …] [CHARACTER SET …] [COLLATE …]
 *   - ALTER [PLUGGABLE] {DATABASE | SCHEMA} <name> …
 *   - DROP [PLUGGABLE] {DATABASE | SCHEMA} [IF EXISTS] <name>
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice G1 — CREATE / ALTER / DROP DATABASE UI.
 */

const IDENT_RE_SOURCE = '[A-Za-z_][A-Za-z0-9_]*|"[^"]+"|`[^`]+`|\\[[^\\]]+\\]';
const IDENT_RE = new RegExp(`^(${IDENT_RE_SOURCE})`);

export type ParsedParkedDatabaseAction = 'create' | 'alter' | 'drop';

export interface ParsedParkedDatabase {
  readonly action: ParsedParkedDatabaseAction;
  readonly name: string;
  readonly quoted: boolean;
  readonly mysqlSchema: boolean;
  readonly oraclePluggable: boolean;
  readonly ifNotExists: boolean;
  readonly ifExists: boolean;
}

function unquoteIdent(raw: string): { name: string; quoted: boolean } {
  if (raw.length >= 2) {
    const first = raw.charAt(0);
    const last = raw.charAt(raw.length - 1);
    if ((first === '"' && last === '"') || (first === '`' && last === '`')) {
      return { name: raw.slice(1, -1), quoted: true };
    }
    if (first === '[' && last === ']') {
      return { name: raw.slice(1, -1), quoted: true };
    }
  }
  return { name: raw, quoted: false };
}

function skipSpaces(source: string, start: number): number {
  let i = start;
  while (i < source.length && /\s/.test(source.charAt(i))) i++;
  return i;
}

export function parseParkedDatabaseStatement(stmtText: string): ParsedParkedDatabase | null {
  const trimmed = stmtText.trim();
  const upper = trimmed.toUpperCase();

  let action: ParsedParkedDatabaseAction;
  let cursor: number;
  if (upper.startsWith('CREATE')) { action = 'create'; cursor = 'CREATE'.length; }
  else if (upper.startsWith('ALTER')) { action = 'alter'; cursor = 'ALTER'.length; }
  else if (upper.startsWith('DROP')) { action = 'drop'; cursor = 'DROP'.length; }
  else return null;

  cursor = skipSpaces(trimmed, cursor);

  let oraclePluggable = false;
  if (upper.startsWith('PLUGGABLE', cursor)) {
    oraclePluggable = true;
    cursor += 'PLUGGABLE'.length;
    cursor = skipSpaces(trimmed, cursor);
  }

  let mysqlSchema = false;
  if (upper.startsWith('DATABASE', cursor)) {
    cursor += 'DATABASE'.length;
  } else if (upper.startsWith('SCHEMA', cursor)) {
    mysqlSchema = true;
    cursor += 'SCHEMA'.length;
  } else {
    return null;
  }
  cursor = skipSpaces(trimmed, cursor);

  let ifNotExists = false;
  let ifExists = false;
  if (action === 'create' && upper.startsWith('IF', cursor)) {
    const m = /^IF\s+NOT\s+EXISTS\s+/.exec(upper.slice(cursor));
    if (m) {
      ifNotExists = true;
      cursor += m[0].length;
    }
  } else if (action === 'drop' && upper.startsWith('IF', cursor)) {
    const m = /^IF\s+EXISTS\s+/.exec(upper.slice(cursor));
    if (m) {
      ifExists = true;
      cursor += m[0].length;
    }
  }

  const nameMatch = IDENT_RE.exec(trimmed.slice(cursor));
  if (!nameMatch) return null;
  const ident = unquoteIdent(nameMatch[1]!);

  return {
    action,
    name: ident.name,
    quoted: ident.quoted,
    mysqlSchema,
    oraclePluggable,
    ifNotExists,
    ifExists,
  };
}
